import { useMemo, useState } from "react";
import AuthImage from "@/components/AuthImage";

const GENDERS = [
  { v: "female", label: "Wanita" },
  { v: "male", label: "Pria" },
];

const AGE_GROUPS = [
  { v: "adult", label: "Dewasa" },
  { v: "child", label: "Anak" },
];

const chip =
  "px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] border transition-colors";
const chipOff = "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-500";
const chipOn = "border-zinc-900 bg-zinc-950 text-[#FBFBF9]";

/**
 * Pemilih model opsional untuk Content Studio.
 *
 * Nilai kosong berarti tidak ada model, dan itu pilihan yang sah: banyak konten
 * edukasi lebih kuat tanpa orang. Karena itu "Random" sengaja tidak ada di sini
 * — memilih model harus eksplisit supaya hasilnya bisa diulang.
 *
 * value: { enabled, kind: "face" | "saved", refId, savedId }
 */
export default function ModelPicker({
  faceRefs = [],
  savedModels = [],
  value,
  onChange,
  activity = "",
  onActivityChange,
  idPrefix = "cs",
  hint = "Model dipakai sebagai roadway: produk tetap fokus utama.",
  // Label slot. Defaultnya "Pakai model (opsional)" supaya pemakaian tunggal
  // tidak berubah tampilan; slot kedua lewat sini supaya dua baris picker
  // tidak terlihat seperti dua duplikat yang tidak sengaja.
  label = "Pakai model (opsional)",
}) {
  const [tab, setTab] = useState("faces");
  const [gender, setGender] = useState("female");
  const [ageGroup, setAgeGroup] = useState("adult");

  const { enabled, kind, refId, savedId } = value;

  const visible = useMemo(
    () =>
      faceRefs.filter(
        (r) => r.gender === gender && (r.age_group || "adult") === ageGroup,
      ),
    [faceRefs, gender, ageGroup],
  );

  // Seleksi dibersihkan di dalam handler, bukan lewat useEffect. Effect dengan
  // deps objek `value` akan berulang setiap render karena onChange selalu
  // membuat objek baru; handler membuat perubahan terjadi tepat satu kali.
  const setEnabled = (next) =>
    onChange({
      enabled: next,
      kind,
      // Mematikan model harus ikut melepas id, kalau tidak backend masih
      // menerima referensi meski checkbox-nya sudah kosong.
      refId: next ? refId : "",
      savedId: next ? savedId : "",
    });

  const switchTab = (next) => {
    setTab(next);
    onChange({ enabled, kind: next === "faces" ? "face" : "saved", refId: "", savedId: "" });
  };

  // Ganti kategori gender/usia: lepas id yang tidak lagi terlihat supaya tidak
  // terkirim sebagai referensi yang tak terlihat di layar.
  const narrow = (patch) => {
    const nextGender = patch.gender ?? gender;
    const nextAge = patch.ageGroup ?? ageGroup;
    const stillVisible = visible.some(
      (r) => r.ref_id === refId && r.gender === nextGender && (r.age_group || "adult") === nextAge,
    );
    setGender(nextGender);
    setAgeGroup(nextAge);
    if (refId && !stillVisible) onChange({ enabled, kind, refId: "", savedId });
  };

  const choose = (nextRefId, nextSavedId, nextKind) =>
    onChange({ enabled: true, kind: nextKind, refId: nextRefId, savedId: nextSavedId });

  if (!faceRefs.length && !savedModels.length) {
    return (
      <div className="border border-zinc-200 bg-zinc-50 p-4 text-[12px] text-zinc-500">
        Daftar model belum termuat. Cek koneksi lalu muat ulang halaman.
      </div>
    );
  }

  return (
    <div className="border border-zinc-200 bg-white">
      <label className="flex cursor-pointer items-start gap-3 border-b border-zinc-200 p-4">
        <input
          type="checkbox"
          data-testid={`${idPrefix}-use-model`}
          checked={!!enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-zinc-950"
        />
        <span className="min-w-0">
          <span className="block text-[13px] font-medium text-zinc-900">
            {label}
          </span>
          <span className="block text-[11px] leading-relaxed text-zinc-500">
            {hint} Tanpa model pun hasilnya tetap dibuat.
          </span>
        </span>
      </label>

      {enabled && (
        <div className="p-4">
          <div className="flex flex-wrap items-center gap-1.5">
            {GENDERS.map((g) => (
              <button
                key={g.v}
                type="button"
                data-testid={`${idPrefix}-gender-${g.v}`}
                aria-pressed={gender === g.v}
                onClick={() => narrow({ gender: g.v })}
                className={`${chip} ${gender === g.v ? chipOn : chipOff}`}
              >
                {g.label}
              </button>
            ))}
            <span className="mx-1 h-4 w-px bg-zinc-200" />
            {AGE_GROUPS.map((a) => (
              <button
                key={a.v}
                type="button"
                data-testid={`${idPrefix}-age-${a.v}`}
                aria-pressed={ageGroup === a.v}
                onClick={() => narrow({ ageGroup: a.v })}
                className={`${chip} ${ageGroup === a.v ? chipOn : chipOff}`}
              >
                {a.label}
              </button>
            ))}
          </div>

          <div className="mt-3 flex gap-1.5">
            {[
              { v: "faces", label: `Model (${faceRefs.length})` },
              { v: "mine", label: `My Models (${savedModels.length})` },
            ].map((t) => (
              <button
                key={t.v}
                type="button"
                onClick={() => switchTab(t.v)}
                className={`${chip} ${tab === t.v ? chipOn : chipOff}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "faces" ? (
            <div className="mt-3 grid max-h-72 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-5">
              {visible.map((r) => {
                const active = kind === "face" && refId === r.ref_id;
                return (
                  <button
                    key={r.ref_id}
                    type="button"
                    data-testid={`${idPrefix}-ref-${r.ref_id}`}
                    aria-pressed={active}
                    onClick={() => choose(active ? "" : r.ref_id, "", "face")}
                    className={`flex flex-col border text-left ${
                      active
                        ? "border-zinc-950 bg-zinc-950 text-white"
                        : "border-zinc-200 hover:border-zinc-500"
                    }`}
                  >
                    <AuthImage
                      path={r.thumbnail_url || r.thumbnail_path}
                      alt={r.name}
                      className="aspect-[3/4] w-full object-cover"
                    />
                    <span className="px-1.5 py-1 font-mono text-[9px] uppercase tracking-[0.12em]">
                      {r.name}
                    </span>
                  </button>
                );
              })}
              {!visible.length && (
                <p className="col-span-full py-4 text-[12px] text-zinc-500">
                  Tidak ada model untuk kategori ini.
                </p>
              )}
            </div>
          ) : (
            <div className="mt-3 grid max-h-72 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-5">
              {savedModels.map((m) => {
                const active = kind === "saved" && savedId === m.model_id;
                return (
                  <button
                    key={m.model_id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => choose("", active ? "" : m.model_id, "saved")}
                    className={`flex flex-col border text-left ${
                      active
                        ? "border-zinc-950 bg-zinc-950 text-white"
                        : "border-zinc-200 hover:border-zinc-500"
                    }`}
                  >
                    <AuthImage
                      path={m.image_url || m.thumbnail_url || m.thumbnail_path}
                      alt={m.name}
                      className="aspect-[3/4] w-full object-cover"
                    />
                    <span className="px-1.5 py-1 font-mono text-[9px] uppercase tracking-[0.12em]">
                      {m.name || "Model"}
                    </span>
                  </button>
                );
              })}
              {!savedModels.length && (
                <p className="col-span-full py-4 text-[12px] text-zinc-500">
                  Belum ada model tersimpan.
                </p>
              )}
            </div>
          )}

          <div className="mt-4">
            <label
              htmlFor={`${idPrefix}-activity`}
              className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500"
            >
              Aktivitas model (opsional)
            </label>
            <input
              id={`${idPrefix}-activity`}
              data-testid={`${idPrefix}-activity`}
              value={activity}
              onChange={(e) => onActivityChange(e.target.value)}
              placeholder="misal: sedang cuci muka, atau sedang menyodorkan serum ke tangan"
              maxLength={120}
              className="mt-1.5 w-full border border-zinc-200 px-3 py-2 text-[13px] outline-none focus:border-zinc-900"
            />
            <p className="mt-1 text-[11px] text-zinc-500">
              Kosongkan untuk membiarkan AI memutuskan sendiri dari isi konten.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
